/**
 * Game Types Configuration
 * 
 * Centralized configuration for all game types in the platform.
 * Each game type defines its unique properties including theming, routes, 
 * components, features, and API endpoints.
 */

export const GAME_TYPES = {
  classic5050: {
    id: 'classic5050',
    name: 'Classic 50/50',
    route: '/',
    purchaseRoute: '/purchase',
    displayRoute: '/display/classic5050',
    themeColors: {
      primary: 'blue-500',
      secondary: 'cyan-500',
      gradient: 'from-blue-500 via-cyan-500 to-teal-400',
      headerGradient: 'from-blue-600 to-cyan-600',
      accentGradient: 'from-blue-50 to-cyan-50',
      darkAccentGradient: 'from-blue-900/20 to-cyan-900/20',
    },
    charityComponent: 'SplitPotCharity',
    heroConfig: {
      title: 'Win Big, Give Back',
      titleKey: 'classic5050.slide1Title',
      subtitle: 'Play weekly for a chance at winning half the pot',
      subtitleKey: 'classic5050.slide1Subtitle',
      icon: 'DollarSign',
      emoji: '💰',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - Classic 50/50',
      headingKey: 'classic5050.howItWorksHeading',
      description: 'Buy tickets for your chance to win half the pot while supporting charity!',
    },
    slides: [
      {
        title: 'Win Big, Give Back',
        titleKey: 'classic5050.slide1Title',
        subtitle: 'Play weekly for a chance at winning half the pot',
        subtitleKey: 'classic5050.slide1Subtitle',
        bgGradient: 'from-blue-500 via-cyan-500 to-teal-400',
        icon: 'DollarSign',
        stats: '50% to Winner, 50% to Charity',
        statsKey: 'classic5050.slide1Stats',
      },
      {
        title: 'Empowering Youth & Families',
        titleKey: 'classic5050.slide2Title',
        subtitle: 'Every ticket supports community programs',
        subtitleKey: 'classic5050.slide2Subtitle',
        bgGradient: 'from-red-500 via-rose-500 to-pink-500',
        icon: 'Heart',
      },
      {
        title: 'Join Thousands of Players',
        titleKey: 'classic5050.slide3Title',
        subtitle: 'Be part of something bigger - win while making a difference',
        subtitleKey: 'classic5050.slide3Subtitle',
        bgGradient: 'from-emerald-500 via-green-500 to-teal-500',
        icon: 'Users',
      },
    ],
    features: ['split_pot', 'weekly_draws', 'bonus_draws', 'leaderboard'],
    enabledSections: ['hero_carousel', 'hero_section', 'organization_writeup', 'organization_writeup_2', 'charity_banner', 'recent_winners', 'leaderboard', 'bonus_draws', 'partner_banner'],
    pageSectionKey: 'classic5050_home',
    drawApiKey: 'classic5050',
    skeletonVariant: 'classic',
    layoutStyles: ['standard', 'style1', 'style2', 'style3', 'style4', 'style5', 'premium', 'premium-plus', 'v2', 'fluid', 'cinematic'],
  },

  modified5050: {
    id: 'modified5050',
    name: 'Modified 50/50',
    route: '/modified-5050',
    purchaseRoute: '/purchase',
    displayRoute: '/display/modified5050',
    themeColors: {
      primary: 'blue-500',
      secondary: 'cyan-500',
      gradient: 'from-blue-500 via-cyan-500 to-teal-400',
      headerGradient: 'from-blue-600 to-cyan-600',
      accentGradient: 'from-blue-50 to-cyan-50',
      darkAccentGradient: 'from-blue-900/20 to-cyan-900/20',
    },
    charityComponent: 'GrowingImpactCharity',
    heroConfig: {
      title: 'Win Big, Give Back',
      titleKey: 'modified5050.slide1Title',
      subtitle: 'Progressive jackpot charity lottery',
      subtitleKey: 'modified5050.slide1Subtitle',
      icon: 'DollarSign',
      emoji: '💰',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - Modified 50/50',
      headingKey: 'modified5050.howItWorksHeading',
      description: 'This is a progressive jackpot draw. Buy tickets for a chance to win!',
    },
    slides: [
      {
        title: 'Win Big, Give Back',
        titleKey: 'modified5050.slide1Title',
        subtitle: 'Progressive jackpot charity lottery',
        subtitleKey: 'modified5050.slide1Subtitle',
        bgGradient: 'from-blue-500 via-cyan-500 to-teal-400',
        icon: 'DollarSign',
        stats: 'Progressive Jackpot',
        statsKey: 'modified5050.slide1Stats',
      },
      {
        title: 'Empowering Youth & Families',
        titleKey: 'modified5050.slide2Title',
        subtitle: 'Every ticket supports community programs',
        subtitleKey: 'modified5050.slide2Subtitle',
        bgGradient: 'from-red-500 via-rose-500 to-pink-500',
        icon: 'Heart',
      },
      {
        title: 'Join Thousands of Players',
        titleKey: 'modified5050.slide3Title',
        subtitle: 'Be part of something bigger - win while making a difference',
        subtitleKey: 'modified5050.slide3Subtitle',
        bgGradient: 'from-emerald-500 via-green-500 to-teal-500',
        icon: 'Users',
      },
    ],
    features: ['progressive_jackpot', 'weekly_draws', 'leaderboard'],
    enabledSections: ['hero_carousel', 'hero_section', 'organization_writeup', 'organization_writeup_2', 'charity_banner', 'recent_winners', 'leaderboard', 'partner_banner'],
    pageSectionKey: 'modified5050_home',
    drawApiKey: 'modified5050',
    skeletonVariant: 'classic',
    layoutStyles: ['standard', 'style1', 'premium', 'premium-plus', 'v2', 'fluid'],
  },

  progressive5050: {
    id: 'progressive5050',
    name: 'Progressive 50/50',
    route: '/progressive-5050',
    purchaseRoute: '/purchase',
    displayRoute: '/display/progressive5050',
    themeColors: {
      primary: 'purple-600',
      secondary: 'pink-600',
      gradient: 'from-purple-600 via-pink-600 to-red-500',
      headerGradient: 'from-purple-600 to-pink-600',
      accentGradient: 'from-purple-50 to-pink-50',
      darkAccentGradient: 'from-purple-900/20 to-pink-900/20',
    },
    charityComponent: 'GrowingImpactCharity',
    heroConfig: {
      title: '52-Week Commitment',
      titleKey: 'progressive5050.slide1Title',
      subtitle: 'Purchase weekly for one year to stay eligible',
      subtitleKey: 'progressive5050.slide1Subtitle',
      icon: 'TrendingUp',
      emoji: '📈',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - Progressive 50/50',
      headingKey: 'progressive5050.howItWorksHeading',
      description: 'This is a 1-year commitment program. When you buy a ticket, you commit to purchasing weekly for 52 weeks.',
    },
    slides: [
      {
        title: '52-Week Commitment',
        titleKey: 'progressive5050.slide1Title',
        subtitle: 'Purchase weekly for one year to stay eligible',
        subtitleKey: 'progressive5050.slide1Subtitle',
        bgGradient: 'from-purple-600 via-pink-600 to-red-500',
        icon: 'TrendingUp',
        stats: 'Jackpot grows every week you play',
        statsKey: 'progressive5050.slide1Stats',
      },
      {
        title: 'Miss a Week, Prize Rolls Over',
        titleKey: 'progressive5050.slide2Title',
        subtitle: 'Jackpot grows when players miss their weekly purchase',
        subtitleKey: 'progressive5050.slide2Subtitle',
        bgGradient: 'from-orange-600 via-red-600 to-pink-600',
        icon: 'RefreshCw',
      },
      {
        title: 'Stay Committed, Win Big',
        titleKey: 'progressive5050.slide3Title',
        subtitle: 'Complete all 52 weeks for your chance at the jackpot',
        subtitleKey: 'progressive5050.slide3Subtitle',
        bgGradient: 'from-cyan-600 via-blue-600 to-purple-600',
        icon: 'Award',
      },
    ],
    steps: [
      {
        title: 'Sign Up',
        titleKey: 'progressive5050.step1Title',
        description: 'Commit to 52 weekly ticket purchases',
        icon: '1️⃣',
        bgGradient: 'from-purple-50 to-pink-50',
        darkBgGradient: 'from-purple-900/20 to-pink-900/20',
        borderColor: 'purple-200',
        darkBorderColor: 'purple-800',
      },
      {
        title: 'Play Weekly',
        titleKey: 'progressive5050.step2Title',
        description: 'Purchase your ticket each week to stay eligible',
        icon: '2️⃣',
        bgGradient: 'from-blue-50 to-cyan-50',
        darkBgGradient: 'from-blue-900/20 to-cyan-900/20',
        borderColor: 'blue-200',
        darkBorderColor: 'blue-800',
      },
      {
        title: 'Watch It Grow',
        titleKey: 'progressive5050.step3Title',
        description: 'Missed weeks roll over, growing the jackpot',
        icon: '3️⃣',
        bgGradient: 'from-orange-50 to-red-50',
        darkBgGradient: 'from-orange-900/20 to-red-900/20',
        borderColor: 'orange-200',
        darkBorderColor: 'orange-800',
      },
      {
        title: 'Win Big',
        titleKey: 'progressive5050.step4Title',
        description: 'Complete all 52 weeks for your shot at the progressive jackpot',
        icon: '4️⃣',
        bgGradient: 'from-emerald-50 to-green-50',
        darkBgGradient: 'from-emerald-900/20 to-green-900/20',
        borderColor: 'emerald-200',
        darkBorderColor: 'emerald-800',
      },
    ],
    features: ['progressive_jackpot', 'weekly_commitment', 'rollover', 'bonus_draws'],
    enabledSections: ['image_slider', 'hero_carousel', 'hero_section', 'organization_writeup', 'organization_writeup_2', 'rollover_timeline', 'how_it_works', 'recent_winners', 'bonus_draws', 'partner_banner'],
    pageSectionKey: 'progressive5050_home',
    drawApiKey: 'progressive5050',
    skeletonVariant: 'progressive',
    layoutStyles: ['standard', 'style2', 'style3', 'v2', 'premium-plus', 'fluid'],
    weeklyCommitment: {
      totalWeeks: 52,
      enabled: true,
    },
  },

  chaseTheAce: {
    id: 'chaseTheAce',
    name: 'Chase the Ace',
    route: '/chase-the-ace',
    purchaseRoute: '/chase-the-ace-purchase',
    displayRoute: '/display/chase-the-ace',
    themeColors: {
      primary: 'blue-600',
      secondary: 'indigo-600',
      gradient: 'from-blue-600 to-indigo-600',
      headerGradient: 'from-blue-600 to-cyan-600',
      accentGradient: 'from-blue-50 to-cyan-50',
      darkAccentGradient: 'from-blue-900/20 to-cyan-900/20',
      spotlightGradient: 'from-gray-900 via-blue-950 to-black',
    },
    charityComponent: 'AceOfHeartsCharity',
    heroConfig: {
      title: 'Ready to Flip the Ace?',
      titleKey: 'chaseTheAce.heroTitle',
      subtitle: 'The jackpot climbs every week. One card could change everything.',
      subtitleKey: 'chaseTheAce.heroSubtitle',
      icon: 'Spade',
      emoji: '♠️',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - Chase the Ace',
      headingKey: 'chaseTheAce.howItWorksHeading',
      description: 'Each week, one lucky ticket holder gets to flip a card. Find the jackpot card and win the jackpot!',
    },
    slides: [
      {
        title: 'Chase the Ace',
        titleKey: 'chaseTheAce.slide1Title',
        subtitle: 'Find the jackpot card and win the growing jackpot',
        subtitleKey: 'chaseTheAce.slide1Subtitle',
        bgGradient: 'from-blue-600 via-indigo-600 to-purple-600',
        icon: 'Spade',
        stats: '52 cards, 1 jackpot card to find',
        statsKey: 'chaseTheAce.slide1Stats',
      },
      {
        title: 'Growing Jackpot',
        titleKey: 'chaseTheAce.slide2Title',
        subtitle: 'Every week without the Ace means a bigger prize',
        subtitleKey: 'chaseTheAce.slide2Subtitle',
        bgGradient: 'from-green-600 via-emerald-600 to-teal-600',
        icon: 'TrendingUp',
      },
      {
        title: 'Weekly Draws',
        titleKey: 'chaseTheAce.slide3Title',
        subtitle: 'Buy tickets for each weekly draw',
        subtitleKey: 'chaseTheAce.slide3Subtitle',
        bgGradient: 'from-amber-600 via-orange-600 to-red-600',
        icon: 'Calendar',
      },
    ],
    steps: [
      {
        title: 'Buy a Ticket',
        titleKey: 'chaseTheAce.step1Title',
        description: 'Purchase tickets for the weekly draw',
        icon: '🎟️',
        bgGradient: 'from-blue-50 to-indigo-50',
        darkBgGradient: 'from-blue-900/20 to-indigo-900/20',
        borderColor: 'blue-200',
        darkBorderColor: 'blue-800',
      },
      {
        title: 'Win the Draw',
        titleKey: 'chaseTheAce.step2Title',
        description: 'A winning ticket is drawn each week',
        icon: '✉️',
        bgGradient: 'from-green-50 to-emerald-50',
        darkBgGradient: 'from-green-900/20 to-emerald-900/20',
        borderColor: 'green-200',
        darkBorderColor: 'green-800',
      },
      {
        title: 'Reveal the Envelope',
        titleKey: 'chaseTheAce.step3Title',
        description: "The winner's chosen envelope is opened to reveal the card",
        icon: '✉️',
        bgGradient: 'from-purple-50 to-pink-50',
        darkBgGradient: 'from-purple-900/20 to-pink-900/20',
        borderColor: 'purple-200',
        darkBorderColor: 'purple-800',
      },
      {
        title: 'Find the Ace!',
        titleKey: 'chaseTheAce.step4Title',
        description: 'Find the jackpot card to win the jackpot!',
        icon: '♠️',
        bgGradient: 'from-amber-50 to-yellow-50',
        darkBgGradient: 'from-amber-900/20 to-yellow-900/20',
        borderColor: 'amber-200',
        darkBorderColor: 'amber-800',
      },
    ],
    features: ['card_reveal', 'growing_jackpot', 'weekly_draws', 'bonus_draws', 'card_tracking'],
    enabledSections: ['hero_banner', 'hero_section', 'card_stats', 'how_it_works', 'recent_activity', 'recent_winners', 'bonus_draws', 'partner_banner'],
    pageSectionKey: 'chasetheace_home',
    drawApiKey: 'chaseTheAce',
    skeletonVariant: 'cta',
    layoutStyles: ['standard', 'style1', 'v2', 'premium', 'storybook', 'deckRunner', 'fluid'],
    cardDeck: {
      totalCards: 52,
      targetCard: 'Configured jackpot card',
    },
  },

  eliminationDraw: {
    id: 'eliminationDraw',
    name: 'Elimination Draw',
    route: '/elimination-draw',
    purchaseRoute: '/purchase',
    displayRoute: '/display/elimination',
    themeColors: {
      primary: 'blue-600',
      secondary: 'purple-600',
      gradient: 'from-blue-600 via-purple-600 to-pink-600',
      headerGradient: 'from-blue-600 via-purple-600 to-pink-600',
      accentGradient: 'from-purple-50 to-pink-50',
      darkAccentGradient: 'from-purple-900/20 to-pink-900/20',
      pageGradient: 'from-blue-50 via-white to-purple-50',
    },
    charityComponent: 'SurvivorShieldCharity',
    heroConfig: {
      title: 'Last One Standing Wins Big',
      titleKey: 'elimination.heroTitle',
      subtitle: 'Multiple prizes along the way, with the final ticket winning the grand prize!',
      subtitleKey: 'elimination.heroSubtitle',
      icon: 'Target',
      emoji: '🎯',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - Elimination Draw',
      headingKey: 'elimination.howItWorksHeading',
      description: 'Tickets are eliminated one by one until only one remains. Win prizes at milestone positions!',
    },
    slides: [
      {
        title: 'Last One Standing',
        titleKey: 'elimination.slide1Title',
        subtitle: 'Tickets are eliminated round by round — survive to win!',
        subtitleKey: 'elimination.slide1Subtitle',
        bgGradient: 'from-blue-600 via-purple-600 to-pink-600',
        icon: 'Target',
        stats: 'Multiple milestone prizes along the way',
        statsKey: 'elimination.slide1Stats',
      },
      {
        title: 'Milestone Prizes',
        titleKey: 'elimination.slide2Title',
        subtitle: 'Win prizes at key elimination milestones',
        subtitleKey: 'elimination.slide2Subtitle',
        bgGradient: 'from-amber-600 via-orange-600 to-red-600',
        icon: 'Award',
      },
      {
        title: 'Grand Prize',
        titleKey: 'elimination.slide3Title',
        subtitle: 'The last ticket standing wins the grand prize!',
        subtitleKey: 'elimination.slide3Subtitle',
        bgGradient: 'from-emerald-600 via-green-600 to-teal-600',
        icon: 'Trophy',
      },
    ],
    steps: [
      {
        title: 'Buy Tickets',
        titleKey: 'elimination.step1Title',
        description: 'Purchase tickets to enter the elimination draw',
        icon: '🎟️',
        bgGradient: 'from-blue-50 to-purple-50',
        darkBgGradient: 'from-blue-900/20 to-purple-900/20',
        borderColor: 'blue-200',
        darkBorderColor: 'blue-800',
      },
      {
        title: 'Eliminations Begin',
        titleKey: 'elimination.step2Title',
        description: 'Tickets are randomly eliminated at regular intervals',
        icon: '🎯',
        bgGradient: 'from-orange-50 to-red-50',
        darkBgGradient: 'from-orange-900/20 to-red-900/20',
        borderColor: 'orange-200',
        darkBorderColor: 'orange-800',
      },
      {
        title: 'Win Milestones',
        titleKey: 'elimination.step3Title',
        description: 'Earn prizes when elimination hits key milestones',
        icon: '🏅',
        bgGradient: 'from-amber-50 to-yellow-50',
        darkBgGradient: 'from-amber-900/20 to-yellow-900/20',
        borderColor: 'amber-200',
        darkBorderColor: 'amber-800',
      },
      {
        title: 'Last Ticket Wins!',
        titleKey: 'elimination.step4Title',
        description: 'The final remaining ticket wins the grand prize!',
        icon: '🏆',
        bgGradient: 'from-emerald-50 to-green-50',
        darkBgGradient: 'from-emerald-900/20 to-green-900/20',
        borderColor: 'emerald-200',
        darkBorderColor: 'emerald-800',
      },
    ],
    features: ['elimination_rounds', 'milestone_prizes', 'grand_prize', 'live_elimination'],
    enabledSections: ['hero_banner', 'hero_section', 'prize_structure', 'elimination_tracker', 'recent_winners', 'partner_banner'],
    pageSectionKey: 'elimination_home',
    drawApiKey: 'eliminationDraw',
    skeletonVariant: 'default',
    layoutStyles: ['standard', 'style1', 'style2', 'v2', 'fluid'],
    elimination: {
      defaultFinalPrize: 50000,
      defaultPrizeStructure: [
        { position: 1000, description: 'When 1000 tickets remain', amount: 100 },
        { position: 750, description: 'When 750 tickets remain', amount: 250 },
        { position: 500, description: 'When 500 tickets remain', amount: 500 },
        { position: 250, description: 'When 250 tickets remain', amount: 1000 },
        { position: 100, description: 'When 100 tickets remain', amount: 2500 },
        { position: 50, description: 'When 50 tickets remain', amount: 5000 },
        { position: 10, description: 'When 10 tickets remain', amount: 10000 },
        { position: 1, description: 'Final ticket standing', amount: 50000 },
      ],
    },
  },

  prizeDraw: {
    id: 'prizeDraw',
    name: 'Prize Draw',
    route: '/prize-draw',
    purchaseRoute: '/purchase',
    displayRoute: '/display/prize-draw',
    themeColors: {
      primary: 'purple-600',
      secondary: 'indigo-600',
      gradient: 'from-purple-600 via-violet-600 to-indigo-600',
      headerGradient: 'from-purple-600 to-indigo-600',
      accentGradient: 'from-purple-50 to-indigo-50',
      darkAccentGradient: 'from-purple-900/20 to-indigo-900/20',
    },
    charityComponent: 'GiftBoxCharity',
    heroConfig: {
      title: 'Win Amazing Prizes',
      titleKey: 'prizeDraw.slide1Title',
      subtitle: 'Enter for your chance to win incredible prizes every week',
      subtitleKey: 'prizeDraw.slide1Subtitle',
      icon: 'Gift',
      emoji: '🎁',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - Prize Draw',
      headingKey: 'prizeDraw.howItWorksHeading',
      description: 'Buy tickets for a chance to win incredible prizes. Each draw features a different prize!',
    },
    slides: [
      {
        title: 'Win Amazing Prizes',
        titleKey: 'prizeDraw.slide1Title',
        subtitle: 'Enter for your chance to win incredible prizes every week',
        subtitleKey: 'prizeDraw.slide1Subtitle',
        bgGradient: 'from-purple-600 via-violet-600 to-indigo-600',
        icon: 'Gift',
        stats: 'Multiple prizes in every draw',
        statsKey: 'prizeDraw.slide1Stats',
      },
      {
        title: 'Support Your Community',
        titleKey: 'prizeDraw.slide2Title',
        subtitle: 'Every ticket helps fund important local causes',
        subtitleKey: 'prizeDraw.slide2Subtitle',
        bgGradient: 'from-pink-600 via-rose-600 to-red-600',
        icon: 'Heart',
      },
      {
        title: 'Easy & Secure',
        titleKey: 'prizeDraw.slide3Title',
        subtitle: 'Buy tickets online anytime, anywhere',
        subtitleKey: 'prizeDraw.slide3Subtitle',
        bgGradient: 'from-amber-600 via-orange-600 to-yellow-600',
        icon: 'Shield',
      },
    ],
    features: ['physical_prizes', 'bonus_draws', 'multiple_prize_tiers'],
    enabledSections: ['hero_carousel', 'hero_section', 'organization_writeup', 'organization_writeup_2', 'image_slider', 'jackpot_card', 'jackpot_summary', 'quick_stats', 'how_it_works', 'recent_winners', 'stats_winners', 'about_text', 'compact_winners', 'charity_impact', 'goal_thermometer', 'charity_banner', 'leaderboard', 'winners_banner', 'top_partners', 'recent_supporters', 'purchase_summary', 'bonus_draws', 'partner_banner', 'draw_sponsor', 'final_cta', 'content_block_1', 'content_block_2', 'content_block_3', 'spacer_1', 'spacer_2', 'spacer_3'],
    pageSectionKey: 'prizedraw_home',
    drawApiKey: 'prizeDraw',
    skeletonVariant: 'default',
    layoutStyles: ['standard', 'style1', 'style2', 'v2', 'fluid'],
  },

  squareGame: {
    id: 'squareGame',
    name: '100 Square Game',
    route: '/square-game',
    purchaseRoute: '/square-game-purchase',
    displayRoute: '/display/square-game',
    themeColors: {
      primary: 'emerald-600',
      secondary: 'green-600',
      gradient: 'from-emerald-600 to-green-600',
      headerGradient: 'from-emerald-600 to-green-600',
      accentGradient: 'from-emerald-50 to-green-50',
      darkAccentGradient: 'from-emerald-900/20 to-green-900/20',
      darkHeaderGradient: 'from-gray-900 via-gray-800 to-gray-900',
    },
    charityComponent: 'CharitySidebar',
    heroConfig: {
      title: 'Pick Your Lucky Square',
      titleKey: 'squareGame.heroTitle',
      subtitle: 'Choose from 100 squares and win big!',
      subtitleKey: 'squareGame.heroSubtitle',
      icon: 'Grid3x3',
      emoji: '🎲',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - 100 Square Game',
      headingKey: 'squareGame.howItWorksHeading',
      description: 'Select your lucky square from the 100-square grid. Numbers are assigned after all squares are sold.',
    },
    slides: [
      {
        title: 'Pick Your Lucky Square',
        titleKey: 'squareGame.slide1Title',
        subtitle: 'Choose from 100 squares on the grid',
        subtitleKey: 'squareGame.slide1Subtitle',
        bgGradient: 'from-emerald-600 to-green-600',
        icon: 'Grid3x3',
        stats: '100 squares, 1 grand prize winner',
        statsKey: 'squareGame.slide1Stats',
      },
      {
        title: 'Track in Real-Time',
        titleKey: 'squareGame.slide2Title',
        subtitle: 'Watch the grid fill up as squares are claimed',
        subtitleKey: 'squareGame.slide2Subtitle',
        bgGradient: 'from-blue-600 via-cyan-600 to-teal-600',
        icon: 'Eye',
      },
      {
        title: 'Win the Grand Prize',
        titleKey: 'squareGame.slide3Title',
        subtitle: 'One lucky square wins it all!',
        subtitleKey: 'squareGame.slide3Subtitle',
        bgGradient: 'from-amber-600 via-orange-600 to-red-600',
        icon: 'Trophy',
      },
    ],
    steps: [
      {
        title: 'Choose Square',
        titleKey: 'squareGame.step1Title',
        description: 'Select your lucky square from the 100-square grid',
        icon: '1️⃣',
        bgGradient: 'from-amber-50 to-yellow-50',
        darkBgGradient: 'from-amber-900/20 to-yellow-900/20',
        borderColor: 'amber-200',
        darkBorderColor: 'amber-800',
      },
      {
        title: 'Watch Game',
        titleKey: 'squareGame.step2Title',
        description: 'Numbers are assigned after all squares are sold',
        icon: '2️⃣',
        bgGradient: 'from-blue-50 to-cyan-50',
        darkBgGradient: 'from-blue-900/20 to-cyan-900/20',
        borderColor: 'blue-200',
        darkBorderColor: 'blue-800',
      },
      {
        title: 'Win Big',
        titleKey: 'squareGame.step3Title',
        description: 'Match the final score to win the jackpot',
        icon: '3️⃣',
        bgGradient: 'from-emerald-50 to-green-50',
        darkBgGradient: 'from-emerald-900/20 to-green-900/20',
        borderColor: 'emerald-200',
        darkBorderColor: 'emerald-800',
      },
    ],
    features: ['square_grid', 'live_tracking', 'real_time_updates'],
    enabledSections: ['hero_banner', 'hero_section', 'square_grid', 'how_it_works', 'charity_sidebar', 'recent_winners'],
    pageSectionKey: 'squaregame_home',
    drawApiKey: 'squareGame',
    skeletonVariant: 'default',
    layoutStyles: ['standard', 'gameday', 'charity', 'v2', 'fluid'],
    grid: {
      totalSquares: 100,
      squaresPerRow: 10,
    },
    // API holds squares for 10 minutes (600s). Use 9:30 on the frontend
    // to account for network latency so the UI never shows time remaining
    // after the server has already released the hold.
    holdDurationMs: 9.5 * 60 * 1000,
  },

  superballBingo: {
    id: 'superballBingo',
    name: 'Superball Bingo',
    route: '/superball-bingo',
    purchaseRoute: '/superball-bingo-purchase',
    displayRoute: '/display/superball-bingo',
    themeColors: {
      primary: 'amber-500',
      secondary: 'blue-500',
      gradient: 'from-amber-500 via-yellow-500 to-amber-600',
      headerGradient: 'from-amber-600 to-yellow-600',
      accentGradient: 'from-amber-50 to-yellow-50',
      darkAccentGradient: 'from-amber-900/20 to-yellow-900/20',
      // Bingo ball colors
      ballColors: {
        B: 'blue-500',    // B1-B15
        I: 'red-500',     // I16-I30
        N: 'gray-400',    // N31-N45 (free space area)
        G: 'green-500',   // G46-G60
        O: 'yellow-500',  // O61-O75
        S: 'amber-400',   // Superball (76)
      },
    },
    charityComponent: 'SuperballBingoCharity',
    heroConfig: {
      title: 'Draw the Superball!',
      titleKey: 'superballBingo.heroTitle',
      subtitle: 'Pick 1 ball from 76. Find the golden Superball to win the jackpot!',
      subtitleKey: 'superballBingo.heroSubtitle',
      icon: 'Sparkles',
      emoji: '🎱',
    },
    howItWorks: {
      heading: 'HOW IT WORKS - Superball Bingo',
      headingKey: 'superballBingo.howItWorksHeading',
      description: 'Buy tickets, win the draw, then reach into the cage and pull out one ball. Draw the golden Superball to win the progressive jackpot!',
    },
    slides: [
      {
        title: 'Draw the Superball!',
        titleKey: 'superballBingo.slide1Title',
        subtitle: 'One golden ball among 76. Will you find it?',
        subtitleKey: 'superballBingo.slide1Subtitle',
        bgGradient: 'from-amber-500 via-yellow-500 to-amber-600',
        icon: 'Sparkles',
        stats: '1 in 76 odds to win the jackpot',
        statsKey: 'superballBingo.slide1Stats',
      },
      {
        title: 'Win Every Session',
        titleKey: 'superballBingo.slide2Title',
        subtitle: 'Even without the Superball, win 25% of session sales',
        subtitleKey: 'superballBingo.slide2Subtitle',
        bgGradient: 'from-blue-500 via-indigo-500 to-purple-500',
        icon: 'Trophy',
      },
      {
        title: 'Growing Jackpot',
        titleKey: 'superballBingo.slide3Title',
        subtitle: 'The jackpot grows every session until someone finds the Superball',
        subtitleKey: 'superballBingo.slide3Subtitle',
        bgGradient: 'from-green-500 via-emerald-500 to-teal-500',
        icon: 'TrendingUp',
      },
    ],
    steps: [
      {
        title: 'Buy Tickets',
        titleKey: 'superballBingo.step1Title',
        description: '$1 each or 6 for $5 - valid for current session only',
        icon: '🎟️',
        bgGradient: 'from-amber-50 to-yellow-50',
        darkBgGradient: 'from-amber-900/20 to-yellow-900/20',
        borderColor: 'amber-200',
        darkBorderColor: 'amber-800',
      },
      {
        title: 'Win the Draw',
        titleKey: 'superballBingo.step2Title',
        description: 'Your ticket is drawn from all entries this session',
        icon: '🎯',
        bgGradient: 'from-blue-50 to-indigo-50',
        darkBgGradient: 'from-blue-900/20 to-indigo-900/20',
        borderColor: 'blue-200',
        darkBorderColor: 'blue-800',
      },
      {
        title: 'Reveal Your Ball',
        titleKey: 'superballBingo.step3Title',
        description: 'Your chosen ball is revealed — is it the hidden Superball?',
        icon: '🎱',
        bgGradient: 'from-purple-50 to-pink-50',
        darkBgGradient: 'from-purple-900/20 to-pink-900/20',
        borderColor: 'purple-200',
        darkBorderColor: 'purple-800',
      },
      {
        title: 'Win Big!',
        titleKey: 'superballBingo.step4Title',
        description: 'Superball = Jackpot! Any other ball = 25% session prize',
        icon: '🏆',
        bgGradient: 'from-green-50 to-emerald-50',
        darkBgGradient: 'from-green-900/20 to-emerald-900/20',
        borderColor: 'green-200',
        darkBorderColor: 'green-800',
      },
    ],
    features: ['progressive_jackpot', 'session_prize', 'ball_tracking', 'animated_draw', 'bonus_draws'],
    enabledSections: ['hero_carousel', 'hero_section', 'ball_tracker', 'jackpot_display', 'how_it_works', 'session_info', 'recent_winners', 'charity_banner'],
    pageSectionKey: 'superballbingo_home',
    drawApiKey: 'superballBingo',
    skeletonVariant: 'default',
    layoutStyles: ['standard', 'style1', 'style2', 'style3', 'liveBoard', 'charity', 'v2', 'fluid'],
    prizeStructure: {
      charityPercent: 50,
      jackpotPercent: 25,
      sessionPrizePercent: 25,
      seedAmount: 500,
    },
    ballConfig: {
      totalBalls: 76,
      regularBalls: 75,
      superballNumber: 76,
      ballsPerLetter: 15,
      letters: ['B', 'I', 'N', 'G', 'O'],
      letterRanges: {
        B: [1, 15],
        I: [16, 30],
        N: [31, 45],
        G: [46, 60],
        O: [61, 75],
      },
    },
  },
};

/**
 * Get configuration for a specific game type
 * @param {string} gameTypeId - The game type identifier (e.g., 'classic5050', 'chaseTheAce')
 * @returns {Object|null} The game type configuration or null if not found
 */
export function getGameTypeConfig(gameTypeId) {
  return GAME_TYPES[gameTypeId] || null;
}

/**
 * Get game type by route path
 * @param {string} routePath - The route path (e.g., '/chase-the-ace')
 * @returns {Object|null} The game type configuration or null if not found
 */
export function getGameTypeByRoute(routePath) {
  return Object.values(GAME_TYPES).find(game => game.route === routePath) || null;
}

/**
 * Get game type by draw API key
 * @param {string} apiKey - The draw API key (e.g., 'chaseTheAce')
 * @returns {Object|null} The game type configuration or null if not found
 */
export function getGameTypeByApiKey(apiKey) {
  return Object.values(GAME_TYPES).find(game => game.drawApiKey === apiKey) || null;
}

/**
 * Get all game types as an array
 * @returns {Array} Array of all game type configurations
 */
export function getAllGameTypes() {
  return Object.values(GAME_TYPES);
}

/**
 * Get game type IDs
 * @returns {Array<string>} Array of game type identifiers
 */
export function getGameTypeIds() {
  return Object.keys(GAME_TYPES);
}

/**
 * Check if a game type supports a specific feature
 * @param {string} gameTypeId - The game type identifier
 * @param {string} feature - The feature to check (e.g., 'bonus_draws', 'weekly_commitment')
 * @returns {boolean} Whether the game type supports the feature
 */
export function gameTypeHasFeature(gameTypeId, feature) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.features?.includes(feature) || false;
}

/**
 * Check if a section is enabled for a game type
 * @param {string} gameTypeId - The game type identifier
 * @param {string} sectionName - The section name to check
 * @returns {boolean} Whether the section is enabled
 */
export function isSectionEnabled(gameTypeId, sectionName) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.enabledSections?.includes(sectionName) || false;
}

/**
 * Get theme colors for a game type
 * @param {string} gameTypeId - The game type identifier
 * @returns {Object|null} The theme colors object or null
 */
export function getGameTypeTheme(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.themeColors || null;
}

/**
 * Get hero configuration for a game type
 * @param {string} gameTypeId - The game type identifier
 * @returns {Object|null} The hero configuration or null
 */
export function getGameTypeHero(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.heroConfig || null;
}

/**
 * Get slides configuration for a game type
 * @param {string} gameTypeId - The game type identifier
 * @returns {Array} Array of slide configurations
 */
export function getGameTypeSlides(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.slides || [];
}

/**
 * Get display name for a game type
 * @param {string} gameTypeId - The game type identifier
 * @returns {string} The display name or the id if not found
 */
export function getGameTypeName(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.name || gameTypeId;
}

/**
 * Get the charity component name for a game type
 * @param {string} gameTypeId - The game type identifier
 * @returns {string|null} The charity component name or null
 */
export function getCharityComponent(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.charityComponent || null;
}

/**
 * Get available layout styles for a game type
 * @param {string} gameTypeId - The game type identifier
 * @returns {Array<string>} Array of available layout style identifiers
 */
export function getAvailableLayoutStyles(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.layoutStyles || ['style1'];
}

/**
 * Get the page sections key for a game type (used with usePageSections hook)
 * @param {string} gameTypeId - The game type identifier
 * @returns {string|null} The page sections key or null
 */
export function getPageSectionKey(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.pageSectionKey || null;
}

/**
 * Get how-it-works configuration for a game type
 * @param {string} gameTypeId - The game type identifier
 * @returns {Object|null} The how-it-works configuration or null
 */
export function getHowItWorks(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.howItWorks || null;
}

/**
 * Get steps configuration for games that use steps (e.g., squareGame)
 * @param {string} gameTypeId - The game type identifier
 * @returns {Array} Array of step configurations
 */
export function getGameSteps(gameTypeId) {
  const gameType = getGameTypeConfig(gameTypeId);
  return gameType?.steps || [];
}

/**
 * Canonical game type alias mapping.
 * Maps various string representations (API values, URL slugs, display names)
 * to their canonical game type IDs used throughout the app.
 */
export const gameTypeAliases = {
  classic5050: 'classic5050',
  'classic5050-adhoc': 'classic5050',
  modified5050: 'modified5050',
  'classic 50/50': 'classic5050',
  'modified 50/50': 'modified5050',
  progressive5050: 'progressive5050',
  'progressive 50/50': 'progressive5050',
  prizedraw: 'prizeDraw',
  'prize draw': 'prizeDraw',
  'prize_draw': 'prizeDraw',
  'chase-the-ace': 'chaseTheAce',
  chasetheace: 'chaseTheAce',
  'chase the ace': 'chaseTheAce',
  elimination: 'eliminationDraw',
  eliminationdraw: 'eliminationDraw',
  'elimination draw': 'eliminationDraw',
  squaregame: 'squareGame',
  'square game': 'squareGame',
  superballbingo: 'superballBingo',
  'superball-bingo': 'superballBingo',
  'superball bingo': 'superballBingo',
};

/**
 * Normalize a game type string to its canonical ID.
 * Handles case-insensitive matching, spaces, hyphens, and slashes.
 * @param {string} value - Raw game type string from API, URL, config, etc.
 * @returns {string|null} Canonical game type ID, or null if unrecognized
 */
export function normalizeGameType(value) {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const key = value.trim().toLowerCase();
  const stripped = key.replace(/[\s\-\/]/g, '');
  return gameTypeAliases[key] || gameTypeAliases[stripped] || null;
}

export default GAME_TYPES;
